// UI del version-guard, en DOM plano dentro de un shadow root.
//
// Por que no React, si los tres portales son React:
//
//   1. El caso que mas nos importa es justamente aquel en el que la app NO
//      monto — un chunk que ya no existe, un bundle a medias. Un banner que
//      vive dentro del arbol de React desaparece exactamente cuando hace
//      falta.
//   2. `shared/` esta fuera del node_modules de cada portal. Importar React
//      desde acá resolveria a otra copia y romperia los hooks.
//   3. El shadow root aisla en los dos sentidos: el CSS viejo del portal no
//      puede deformar el cartel, y el cartel no puede filtrar estilos.
//
// Todo el estilo es propio y explicito, sin Tailwind: el bundle de CSS que
// esta cargado es, por definicion, el viejo.

import type { GuardState } from './types';

const HOST_ID = 'authsys-version-guard';

export interface UiCallbacks {
  onApply: () => void;
  onDismiss: () => void;
  onRetry: () => void;
}

export interface UiLabels {
  /** Nombre del portal para el copy: "el backoffice", "el portal". */
  appName: string;
}

const CSS = `
:host { all: initial; }
* { box-sizing: border-box; font-family: Inter, system-ui, -apple-system, "Segoe UI", sans-serif; }

.wrap {
  position: fixed;
  right: 16px;
  bottom: 16px;
  z-index: 2147483000;
  display: flex;
  flex-direction: column;
  gap: 10px;
  max-width: min(400px, calc(100vw - 32px));
}
@media (max-width: 520px) {
  .wrap { left: 16px; right: 16px; max-width: none; }
}

.card {
  background: var(--vg-bg);
  color: var(--vg-fg);
  border: 1px solid var(--vg-border);
  border-radius: 12px;
  box-shadow: 0 10px 30px -10px rgba(0,0,0,.35), 0 2px 8px -2px rgba(0,0,0,.2);
  padding: 14px 16px;
  animation: rise .22s cubic-bezier(.2,.8,.3,1);
}
@keyframes rise {
  from { opacity: 0; transform: translateY(8px); }
  to   { opacity: 1; transform: none; }
}
@media (prefers-reduced-motion: reduce) {
  .card { animation: none; }
}

.row { display: flex; align-items: flex-start; gap: 11px; }
.dot {
  flex: none;
  width: 9px; height: 9px;
  margin-top: 5px;
  border-radius: 50%;
  background: var(--vg-accent);
  box-shadow: 0 0 0 3px var(--vg-accent-halo);
}
.dot.ok { background: var(--vg-ok); box-shadow: 0 0 0 3px var(--vg-ok-halo); }

.title { font-size: 14px; font-weight: 600; line-height: 1.35; margin: 0; letter-spacing: -.01em; }
.meta {
  margin: 3px 0 0;
  font-size: 12px;
  line-height: 1.45;
  color: var(--vg-muted);
}
.mono { font-family: ui-monospace, SFMono-Regular, Menlo, monospace; font-size: 11.5px; }

.actions { display: flex; gap: 8px; margin-top: 11px; justify-content: flex-end; }

button {
  font: inherit;
  font-size: 13px;
  font-weight: 550;
  border-radius: 8px;
  padding: 7px 13px;
  border: 1px solid transparent;
  cursor: pointer;
  transition: background-color .13s, border-color .13s, color .13s;
}
button:focus-visible { outline: 2px solid var(--vg-accent); outline-offset: 2px; }

.primary { background: var(--vg-accent); color: #fff; }
.primary:hover { background: var(--vg-accent-hover); }
.ghost { background: transparent; color: var(--vg-muted); border-color: var(--vg-border); }
.ghost:hover { background: var(--vg-hover); color: var(--vg-fg); }

/* Modal bloqueante: el browser insiste en servir lo viejo. */
.scrim {
  position: fixed; inset: 0;
  z-index: 2147483001;
  background: rgba(4,7,15,.62);
  backdrop-filter: blur(3px);
  display: grid; place-items: center;
  padding: 20px;
}
.modal { max-width: 440px; width: 100%; }
.modal .title { font-size: 15px; }
.modal ol { margin: 10px 0 0; padding-left: 18px; font-size: 12.5px; line-height: 1.7; color: var(--vg-muted); }
kbd {
  font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
  font-size: 11px;
  background: var(--vg-hover);
  border: 1px solid var(--vg-border);
  border-bottom-width: 2px;
  border-radius: 4px;
  padding: 1px 5px;
  color: var(--vg-fg);
}
`;

const THEME_LIGHT = `
  --vg-bg: #ffffff;
  --vg-fg: #0f172a;
  --vg-muted: #64748b;
  --vg-border: #e2e8f0;
  --vg-hover: #f1f5f9;
  --vg-accent: #7c3aed;
  --vg-accent-hover: #6d28d9;
  --vg-accent-halo: rgba(124,58,237,.18);
  --vg-ok: #16a34a;
  --vg-ok-halo: rgba(22,163,74,.18);
`;

const THEME_DARK = `
  --vg-bg: #131c2b;
  --vg-fg: #f1f5f9;
  --vg-muted: #94a3b8;
  --vg-border: #263349;
  --vg-hover: #1c2739;
  --vg-accent: #8b5cf6;
  --vg-accent-hover: #a78bfa;
  --vg-accent-halo: rgba(139,92,246,.22);
  --vg-ok: #22c55e;
  --vg-ok-halo: rgba(34,197,94,.2);
`;

function prefersDark(): boolean {
  const root = document.documentElement;
  if (root.classList.contains('dark')) return true;
  if (root.classList.contains('light')) return false;
  const attr = root.getAttribute('data-theme');
  if (attr === 'dark') return true;
  if (attr === 'light') return false;
  return window.matchMedia?.('(prefers-color-scheme: dark)').matches ?? false;
}

/** Fecha del commit en formato corto local. */
function formatDate(iso: string | null): string | null {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleString('es-AR', {
    day: '2-digit',
    month: '2-digit',
    year: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function esc(value: string): string {
  return value.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
}

/** Linea de procedencia: commit + fecha. El control interno visible. */
function provenance(commitShort: string | null, commitDate: string | null): string {
  const parts: string[] = [];
  if (commitShort) parts.push(`<span class="mono">${esc(commitShort)}</span>`);
  const date = formatDate(commitDate);
  if (date) parts.push(esc(date));
  return parts.join(' · ');
}

export function createUi(labels: UiLabels, cb: UiCallbacks): {
  render: (state: GuardState) => void;
  destroy: () => void;
} {
  document.getElementById(HOST_ID)?.remove();

  const host = document.createElement('div');
  host.id = HOST_ID;
  const shadow = host.attachShadow({ mode: 'open' });
  const style = document.createElement('style');
  const container = document.createElement('div');
  shadow.append(style, container);
  document.body.appendChild(host);

  const applyTheme = (): void => {
    style.textContent = `:host { ${prefersDark() ? THEME_DARK : THEME_LIGHT} }\n${CSS}`;
  };
  applyTheme();

  // El toggle de tema de los portales cambia la clase del <html>.
  const themeObserver = new MutationObserver(applyTheme);
  themeObserver.observe(document.documentElement, {
    attributes: true,
    attributeFilter: ['class', 'data-theme'],
  });
  const mq = window.matchMedia?.('(prefers-color-scheme: dark)');
  mq?.addEventListener('change', applyTheme);

  let toastTimer: ReturnType<typeof setTimeout> | null = null;
  let toastShown = false;

  const bind = (): void => {
    container.querySelector('[data-act="apply"]')?.addEventListener('click', cb.onApply);
    container.querySelector('[data-act="dismiss"]')?.addEventListener('click', cb.onDismiss);
    container.querySelector('[data-act="retry"]')?.addEventListener('click', cb.onRetry);
  };

  const render = (state: GuardState): void => {
    const blocks: string[] = [];

    // 1. Trabado: la escalera se agoto. Cartel bloqueante, ultima instancia.
    if (state.status === 'stuck') {
      const combo = /Mac|iPhone|iPad/.test(navigator.platform || navigator.userAgent)
        ? '<kbd>⌘</kbd> + <kbd>Shift</kbd> + <kbd>R</kbd>'
        : '<kbd>Ctrl</kbd> + <kbd>Shift</kbd> + <kbd>R</kbd>';
      container.innerHTML = `
        <div class="scrim">
          <div class="card modal" role="alertdialog" aria-modal="true" aria-labelledby="vg-t">
            <div class="row">
              <span class="dot"></span>
              <div>
                <p class="title" id="vg-t">No pudimos aplicar la actualización</p>
                <p class="meta">Tu navegador sigue devolviendo la versión anterior de ${esc(
                  labels.appName,
                )} aunque hay una más nueva publicada.</p>
                <ol>
                  <li>Recargá forzando la caché con ${combo}.</li>
                  <li>Si sigue igual, cerrá la pestaña y volvé a abrirla.</li>
                </ol>
              </div>
            </div>
            <div class="actions">
              <button class="primary" data-act="retry">Reintentar</button>
            </div>
          </div>
        </div>`;
      bind();
      return;
    }

    // 2. Toast de exito. Solo cuando hubo actualizacion real: si ya estabas
    //    en la ultima version, no hay nada que informar.
    if (state.justUpdated && !toastShown) {
      toastShown = true;
      blocks.push(`
        <div class="card" role="status" aria-live="polite" data-block="toast">
          <div class="row">
            <span class="dot ok"></span>
            <div>
              <p class="title">Ya estás en la última versión</p>
              <p class="meta">${
                provenance(state.local.commitShort, state.local.commitDate) || 'Actualizado'
              }</p>
            </div>
          </div>
        </div>`);
      if (toastTimer) clearTimeout(toastTimer);
      toastTimer = setTimeout(() => {
        container.querySelector('[data-block="toast"]')?.remove();
      }, 7000);
    }

    // 3. Banner de actualizacion disponible.
    const showBanner =
      state.status === 'update-available' && (!state.dismissed || !state.dismissible);
    if (showBanner && state.remote) {
      const urgent = !state.dismissible;
      const detail = provenance(state.remote.commitShort, state.remote.commitDate);
      blocks.push(`
        <div class="card" role="status" aria-live="polite">
          <div class="row">
            <span class="dot"></span>
            <div>
              <p class="title">${
                urgent
                  ? 'Actualización pendiente'
                  : `Hay una versión nueva de ${esc(labels.appName)}`
              }</p>
              <p class="meta">${
                urgent
                  ? 'Estás usando una versión anterior. Recargá para continuar con la actual.'
                  : detail || 'Recargá para tenerla.'
              }</p>
              ${urgent && detail ? `<p class="meta">${detail}</p>` : ''}
            </div>
          </div>
          <div class="actions">
            ${
              state.dismissible
                ? '<button class="ghost" data-act="dismiss">Después</button>'
                : ''
            }
            <button class="primary" data-act="apply">Actualizar ahora</button>
          </div>
        </div>`);
    }

    // 4. Aplicando.
    if (state.status === 'reloading') {
      blocks.push(`
        <div class="card" role="status" aria-live="polite">
          <div class="row">
            <span class="dot"></span>
            <div><p class="title">Aplicando actualización…</p></div>
          </div>
        </div>`);
    }

    container.innerHTML = blocks.length > 0 ? `<div class="wrap">${blocks.join('')}</div>` : '';
    bind();
  };

  return {
    render,
    destroy() {
      themeObserver.disconnect();
      mq?.removeEventListener('change', applyTheme);
      if (toastTimer) clearTimeout(toastTimer);
      host.remove();
    },
  };
}
